import _ from 'lodash';
import moment from 'moment';

import React, {
    useContext
} from 'react';

import PropTypes from 'prop-types';

import {
    useIsExportingPPT,
    useRegisterSlideElementsForPPTExport
} from '../../../hooks/useExport.js';

import {
    useSetExportConfig
} from '../../../hooks/exportConfigurationHooks.js';

import {
    useTranslation
} from '../../../../i18n.js';

import LandscapeContext from '../../../contexts/landscapeContext.js';

import MetricLoadingView from '../../../components/MetricLoadingView.jsx';
import Panel from '../../../components/Panel.jsx';
import Label from '../../../components/Label.jsx';

import {
    RIQPopover
} from '../../../components/RIQPopover.jsx';

import {
    DeltaRelativeInsight,
    NumericalValue,
    Summary
} from '../../../components/Summary.jsx';

import {
    Bar
} from '../../../components/charts/Bar.jsx';

import {
    useComputedCurationConfigValue,
    useCurationConfigValue
} from '../../../hooks/widgetConfiguration.js';

function getNumericMetricData(request, result) {
    return {
        raw: _.last(_.last(result.data)),
        value: _.last(_.last(RivalIQ.metrics.new.MetricFormatter.getFormattedResults(request, result, RivalIQ.metrics.new.enums.MetricFormatTypes.expanded))),
        formatted: _.last(_.last(RivalIQ.metrics.new.MetricFormatter.getFormattedResults(request, result, RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated)))
    };
}

function ActivitySummary({requests, results}) {
    let insight = null;
    const delta = getNumericMetricData(requests.totalActivityDelta, results.totalActivityDelta);
    if (delta.raw !== null) {
        insight = (
            <DeltaRelativeInsight
                formatted={delta.formatted}
                value={delta.raw}
            />
        );
    }

    return (
        <Summary
            description='The number of published videos.'
            title='Videos'
            value={<NumericalValue {...getNumericMetricData(requests.totalActivity, results.totalActivity)} />}
            insight={insight}
        />
    );
}

function VideoViewSummary({requests, results}) {
    let insight = null;
    const delta = getNumericMetricData(requests.totalVideoViewsDelta, results.totalVideoViewsDelta);
    if (delta.raw !== null) {
        insight = (
            <DeltaRelativeInsight
                formatted={delta.formatted}
                value={delta.raw}
            />
        );
    }

    return (
        <Summary
            description='The total number of video views.'
            title='Video Views'
            value={<NumericalValue {...getNumericMetricData(requests.totalVideoViews, results.totalVideoViews)} />}
            insight={insight}
        />
    );
}

function BarTooltipWithLabel(label) {
    return (function BarTooltip({payload}) {
        if (!payload || !payload.length) {
            return;
        }

        const placement = null;
        const datum = _.last(_.first(payload).payload);

        return (
            <RIQPopover
                triggerNode={<div/>}
                className='tlw-flex tlw-w-full tlw-items-center tlw-gap-[10px] tlw-text-md tlw-font-normal'
                placement={placement || 'top'}
                portalNode={document.body}
                additionalHookProps={{
                    visible: true
                }}
            >
                <div>
                    <div className='tlw-m-0 tlw-whitespace-nowrap tlw-rounded-t-md tlw-border tlw-border-l-0 tlw-border-r-0 tlw-border-t-0 tlw-border-solid tlw-border-gray-200 tlw-px-7 tlw-py-4 tlw-text-md tlw-text-gray-700'>
                        {datum.title}
                    </div>
                    <div className='tlw-py-4.5'>
                        <div className='tlw-border-0 tlw-border-solid tlw-border-gray-200 tlw-px-7 tlw-py-4 tlw-text-left even_tlw-border-l'>
                            {label}
                            <div className='tlw-text-xl tlw-font-bold tlw-leading-36 tlw-text-gray-900'>
                                {datum.value}
                            </div>
                        </div>
                    </div>
                </div>
            </RIQPopover>
        );
    });
}

function ActivityByInterval({interval, requests, results}) {
    const timezone = requests.activityByInterval.filters.timePeriod.timezone;
    const data = _.map(results.activityByInterval.data, ([day, value]) => {
        const startDate = moment.tz(day, timezone);

        const title = startDate.format('MMM D, YYYY');
        return [
            startDate.format('MM/DD'),
            value,
            false,
            {
                title,
                value
            }
        ];
    });

    return (
        <>
            <Label>Videos Per {interval}</Label>
            <Bar
                data={data}
                tooltip={BarTooltipWithLabel(<Label>Videos Per {interval}</Label>)}
                yAxisAllowDecimals={false}
            />
        </>
    );
}

function VideoViewsByInterval({interval, requests, results}) {
    const timezone = requests.videoViewsByInterval.filters.timePeriod.timezone;
    const data = _.map(results.videoViewsByInterval.data, ([day, value]) => {
        const startDate = moment.tz(day, timezone);

        const title = startDate.format('MMM D, YYYY');
        return [
            startDate.format('MM/DD'),
            value,
            false,
            {
                title,
                value
            }
        ];
    });

    return (
        <>
            <Label>Video Views Per {interval}, By Published Day</Label>
            <Bar
                data={data}
                tooltip={BarTooltipWithLabel(<Label>Videos Views Per {interval}</Label>)}
                yAxisAllowDecimals={false}
            />
        </>
    );
}

export default function ActivityAndVideoViews({instanceId, interval = 'day', pdfExportConfig, appSpecificExportConfig}) {
    const isPPTExport = useIsExportingPPT();

    const landscape = useContext(LandscapeContext);

    const {
        t
    } = useTranslation('apps/insights/components/ActivityAndVideoViews');

    const timezone = useCurationConfigValue(instanceId, 'timezone');
    const companyId = useCurationConfigValue(instanceId, 'companyId');
    const dateRanges = useComputedCurationConfigValue(instanceId, 'dateRanges');
    const searchTerm = useCurationConfigValue(instanceId, 'searchTerm');

    const title = t('title');

    useSetExportConfig(instanceId, {
        title,
        supportedExportFormats: _.without(_.values(RivalIQ.enums.exportFormat), RivalIQ.enums.exportFormat.CSV),
        supportedExportTypes: _.values(RivalIQ.enums.exportType),
        pdfExportConfig,
        appSpecificExportConfig
    });

    useRegisterSlideElementsForPPTExport(instanceId, {
        header: {
            title,
            subtitle: appSpecificExportConfig.timePeriodStr
        }
    });

    const metricRequests = {
        totalActivity: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_POST_DATA,
            dimensions: [],
            measure: 'distinctCount(post_id)',
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            }
        },
        totalActivityDelta: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_POST_DATA,
            dimensions: [],
            measure: {
                field: 'distinctCount(post_id)',
                transform: 'deltaRelative',
                transformConfig: {
                    comparePeriodStartDate: dateRanges.compare.start,
                    comparePeriodEndDate: dateRanges.compare.end
                }
            },
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            }
        },
        totalVideoViews: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_POST_DATA,
            dimensions: [],
            measure: 'sum(video_views)',
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            }
        },
        totalVideoViewsDelta: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_POST_DATA,
            dimensions: [],
            measure: {
                field: 'sum(video_views)',
                transform: 'deltaRelative',
                transformConfig: {
                    comparePeriodStartDate: dateRanges.compare.start,
                    comparePeriodEndDate: dateRanges.compare.end
                }
            },
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            }
        },
        activityByInterval: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_POST_DATA,
            dimensions: ['day'],
            measure: 'distinctCount(post_id)',
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            }
        },
        videoViewsByInterval: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_POST_DATA,
            dimensions: ['day'],
            measure: 'sum(video_views)',
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            }
        }
    };

    if (searchTerm) {
        for (const request of _.values(metricRequests)) {
            request.filters.publicPost = {
                searchTerm: searchTerm
            };
        }
    }

    return (
        <Panel
            instanceId={instanceId}
            title={title}
            exportSubtitle={appSpecificExportConfig.timePeriodStr}
            description={t('description')}
            showViewMore={false}
        >
            <MetricLoadingView
                instanceId={instanceId}
                metricRequests={metricRequests}>
                {
                    (metricResults) => {

                        return (
                            <article>
                                <section className='tlw-grid tlw-grid-cols-2 tlw-border-[0] tlw-border-b tlw-border-solid tlw-border-gray-350 tlw-px-7.5 tlw-py-5'>
                                    <ActivitySummary
                                        requests={metricRequests}
                                        results={metricResults}
                                    />
                                    <VideoViewSummary
                                        requests={metricRequests}
                                        results={metricResults}
                                    />
                                </section>
                                <section className='tlw-border-[0] tlw-border-b tlw-border-solid tlw-border-gray-350 tlw-px-7.5 tlw-py-5'>
                                    <ActivityByInterval
                                        interval={interval}
                                        requests={metricRequests}
                                        results={metricResults}
                                    />
                                </section>
                                <section className={isPPTExport ? 'tlw-break-before-page' : 'tlw-px-7.5 tlw-py-5'}>
                                    <VideoViewsByInterval
                                        interval={interval}
                                        requests={metricRequests}
                                        results={metricResults}
                                    />
                                </section>
                            </article>
                        );
                    }
                }
            </MetricLoadingView>
        </Panel>
    );
}

ActivityAndVideoViews.propTypes = {
    appSpecificExportConfig: PropTypes.object
};
